/* Where you are inside what is loaded, and how to move within it.
 *
 * ---------------------------------------------------------------------------
 * WHY A SECOND TIMELINE, WHEN THERE IS ALREADY A DAY STRIP
 *
 * They answer different questions and neither can answer the other's.
 *
 * The day strip is twenty-four hours wide. On a phone that is about three
 * hundred pixels, so one pixel is roughly five minutes and the smallest move a
 * finger can make is larger than most of what anybody wants to hear. It is a
 * map: it says where in the day there is sound and it gets you to the right
 * hour. It cannot get you from 12:00 to 12:32.
 *
 * This is the transport. It spans the thing you are inside -- one clip, or the
 * hour you landed in -- and across the same three hundred pixels that is a
 * pixel every twelve seconds. It is the bar off a music player, and it is the
 * bar off a music player on purpose: everybody already knows that a knob on a
 * line can be dragged and that the filled part is what has been played.
 *
 * Two levels is what every product in this genre converges on -- an NVR's day
 * bar over its hour bar, a dashcam's trip list over its clip scrubber -- and
 * they converge on it because one bar cannot hold both a day and a minute.
 *
 * ---------------------------------------------------------------------------
 * THREE LAYERS, WHICH IS THE FAMILIAR ONE
 *
 *   track     the window, whether or not anything was recorded in it
 *   have      the parts that are actually in storage and will make a sound
 *   played    from the start of the window to the playhead
 *
 * The middle layer is the one a music player does not need and this does. A
 * recording day has gaps -- a node reboots, an uplink drops, a janitor eats the
 * oldest hour -- and a bar that drew an unbroken line over them would promise
 * sixty minutes of audio where there are forty. Rule 4: a gap is a fact, and it
 * is drawn.
 *
 * Seeking into a gap moves forward to the next thing that exists rather than
 * landing in silence, for the reason the day strip refuses a click on an empty
 * hour: silence the player caused and silence the sea made are identical at the
 * speaker, and only one of them is worth reporting.
 */
import { useCallback, useRef, useState } from 'react'
import { useT } from '../lang'
import type { Segment } from '../segments'
import { clock } from '../time'

const pct = (n: number) => `${Math.max(0, Math.min(100, n * 100))}%`

/** Keyboard steps. Five seconds is about the shortest move worth making in a
 *  recording of the sea; a minute with shift crosses a whole segment. */
const STEP_S = 5
const BIG_STEP_S = 60

export function Transport({
  t0, t1, at, segments, onSeek,
}: {
  /** The window, in epoch seconds. */
  t0: number
  t1: number
  /** The playhead, in epoch seconds. */
  at: number
  /** Everything loaded, so the reachable parts of the window can be drawn. */
  segments: Segment[]
  onSeek: (epoch: number) => void
}) {
  const t = useT()
  const bar = useRef<HTMLDivElement | null>(null)
  const [dragging, setDragging] = useState(false)
  const span = Math.max(1, t1 - t0)

  /* The runs of the window that hold audio.
   *
   * Merged where segments touch, so a contiguous hour is one block rather than
   * sixty abutting ones with a hairline between each -- which at this width
   * would draw as a grey wash and read as no coverage at all. */
  const runs: [number, number][] = []
  for (const s of segments) {
    const a = Math.max(t0, s.startUtc)
    const b = Math.min(t1, s.startUtc + s.durationS)
    if (b <= a) continue
    const last = runs[runs.length - 1]
    // Half a second of tolerance: segment boundaries are stamped by the node
    // and a millisecond of rounding between two of them is not a gap.
    if (last && a - last[1] <= 0.5) last[1] = Math.max(last[1], b)
    else runs.push([a, b])
  }

  const from = useCallback((clientX: number) => {
    const box = bar.current?.getBoundingClientRect()
    if (!box) return t0
    return t0 + Math.max(0, Math.min(1, (clientX - box.left) / box.width)) * span
  }, [t0, span])

  const down = (e: React.PointerEvent) => {
    e.currentTarget.setPointerCapture(e.pointerId)
    setDragging(true)
    onSeek(from(e.clientX))
  }

  const move = (e: React.PointerEvent) => {
    if (!dragging) return
    onSeek(from(e.clientX))
  }

  const up = (e: React.PointerEvent) => {
    if (!dragging) return
    try { e.currentTarget.releasePointerCapture(e.pointerId) } catch { /* gone */ }
    setDragging(false)
  }

  const key = (e: React.KeyboardEvent) => {
    const step = e.shiftKey ? BIG_STEP_S : STEP_S
    if (e.key === 'ArrowLeft') onSeek(Math.max(t0, at - step))
    else if (e.key === 'ArrowRight') onSeek(Math.min(t1, at + step))
    else if (e.key === 'Home') onSeek(t0)
    else if (e.key === 'End') onSeek(t1)
    else return
    e.preventDefault()
  }

  const p = (at - t0) / span

  return (
    <div className={`transport${dragging ? ' dragging' : ''}`}>
      <span className="tp-end num">{clock(t0)}</span>

      <div
        ref={bar}
        className="tp-bar"
        role="slider"
        tabIndex={0}
        aria-label={t('Position in the recording')}
        aria-valuemin={0}
        aria-valuemax={Math.round(span)}
        aria-valuenow={Math.round(at - t0)}
        aria-valuetext={clock(at, true)}
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerCancel={up}
        onKeyDown={key}
      >
        <span className="tp-track" />
        {runs.map(([a, b]) => (
          <span
            key={a}
            className="tp-have"
            style={{ left: pct((a - t0) / span), width: pct((b - a) / span) }}
          />
        ))}
        <span className="tp-played" style={{ width: pct(p) }} />
        <span className="tp-knob" style={{ left: pct(p) }} />
      </div>

      <span className="tp-end num">{clock(t1)}</span>
    </div>
  )
}
